/**
 * 危险操作二次确认
 *
 * 用于特例放行、进项锁定、终止流程、停用租户等不可逆或有合规后果的操作。
 * 设计要点（来自文档要求）：
 *   1. **必须填写理由**才能确认 —— 特例放行、人工接管、退回都要留痕；
 *   2. 提供常见理由快捷选项，减少财务人员打字成本（但最终仍写入操作日志）；
 *   3. 对「不可逆写操作」（进项锁定）额外要求勾选已知晓，避免误点。
 */

import { useEffect, useMemo, useState } from 'react';
import { Alert, Checkbox, Form, Input, Modal, Space, Tag, Typography } from 'antd';
import { ExclamationCircleTwoTone } from '@ant-design/icons';

const { TextArea } = Input;
const { Text, Paragraph } = Typography;

export interface ConfirmDangerProps {
  open: boolean;
  title: string;
  /** 操作后果说明，建议写清「会影响什么、能否撤回」 */
  description?: React.ReactNode;
  /** 危险等级：high 用红色标题，normal 用默认色 */
  level?: 'high' | 'normal';
  /** 理由输入框的标签 */
  reasonLabel?: string;
  /** 理由最少字数 */
  minReasonLength?: number;
  /** 快捷理由（点击填入输入框，仍可编辑） */
  reasonPresets?: string[];
  /** 需要额外勾选确认的文案（不可逆操作） */
  acknowledgeText?: string;
  /** 确认按钮文案 */
  confirmText?: string;
  /** 附加内容（如展示被放行的发票摘要） */
  extra?: React.ReactNode;
  loading?: boolean;
  onConfirm: (reason: string) => void | Promise<void>;
  onCancel: () => void;
}

export function ConfirmDanger({
  open,
  title,
  description,
  level = 'high',
  reasonLabel = '操作理由（必填，将写入操作日志）',
  minReasonLength = 8,
  reasonPresets,
  acknowledgeText,
  confirmText = '确认执行',
  extra,
  loading = false,
  onConfirm,
  onCancel,
}: ConfirmDangerProps): React.ReactElement {
  const [reason, setReason] = useState('');
  const [acknowledged, setAcknowledged] = useState(false);

  useEffect(() => {
    if (open) {
      setReason('');
      setAcknowledged(false);
    }
  }, [open]);

  const reasonOk = reason.trim().length >= minReasonLength;
  const canSubmit = reasonOk && (!acknowledgeText || acknowledged) && !loading;

  const presets = useMemo(() => reasonPresets ?? [], [reasonPresets]);

  return (
    <Modal
      open={open}
      title={
        <Space>
          <ExclamationCircleTwoTone twoToneColor={level === 'high' ? '#cf1322' : '#faad14'} />
          <span style={{ color: level === 'high' ? '#cf1322' : undefined }}>{title}</span>
        </Space>
      }
      okText={confirmText}
      cancelText="取消"
      okButtonProps={{ danger: level === 'high', disabled: !canSubmit }}
      confirmLoading={loading}
      onOk={() => void onConfirm(reason.trim())}
      onCancel={onCancel}
      destroyOnHidden
      maskClosable={false}
      width={560}
    >
      {description ? (
        <Paragraph type="secondary" style={{ marginBottom: 12 }}>
          {description}
        </Paragraph>
      ) : null}

      {extra}

      <Form layout="vertical" style={{ marginTop: 8 }}>
        <Form.Item
          label={reasonLabel}
          required
          validateStatus={reason.length > 0 && !reasonOk ? 'error' : undefined}
          help={
            reason.length > 0 && !reasonOk
              ? `请至少填写 ${minReasonLength} 个字，说明判断依据（审计需要）`
              : `至少 ${minReasonLength} 个字。系统会连同操作人、时间一并留痕，事后可追溯。`
          }
        >
          <TextArea
            rows={3}
            value={reason}
            maxLength={500}
            showCount
            placeholder="例如：发票已冲红，但货物已入库且供应商承诺 3 日内重开，经财务经理同意先行入账。"
            onChange={(e) => setReason(e.target.value)}
          />
        </Form.Item>

        {presets.length > 0 ? (
          <div style={{ marginTop: -12, marginBottom: 12 }}>
            <Text type="secondary" style={{ fontSize: 12 }}>
              快捷填入：
            </Text>
            <Space size={[4, 4]} wrap style={{ marginTop: 4 }}>
              {presets.map((preset) => (
                <Tag
                  key={preset}
                  style={{ cursor: 'pointer' }}
                  onClick={() => setReason(preset)}
                  color={reason === preset ? 'blue' : undefined}
                >
                  {preset}
                </Tag>
              ))}
            </Space>
          </div>
        ) : null}
      </Form>

      {acknowledgeText ? (
        <Alert
          type="warning"
          showIcon
          message={
            <Checkbox checked={acknowledged} onChange={(e) => setAcknowledged(e.target.checked)}>
              {acknowledgeText}
            </Checkbox>
          }
        />
      ) : null}
    </Modal>
  );
}

export default ConfirmDanger;
